import React from "react";
import { Flex, HelpIndicator, Link, Logo, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { generateGpMainUrl } from "~/utils/url";
import { URL_PARAMS, WORKFLOWS_ROUTE } from "../../constants";
import { buildClientUrlFilters, ClientDueDateRangeKey } from "../../filterUtils";
import * as styles from "./styles_vanilla.css";
import { ClientCellRendererParams } from "./types";

export { WorkflowTypeChips } from "../../WorkflowTypeChips";

type ClickableCellParams = ClientCellRendererParams & {
  dueDateRangeKey?: ClientDueDateRangeKey;
  isActiveWorkflows?: boolean;
  /** Provided via cellRendererParams by getColumnDefs — true when crltWorkflowEngine flag is ON. */
  useWorkflowEngine?: boolean;
};

export const ClientWorkspaceCell: React.FC<ClientCellRendererParams> = ({ data }) => {
  if (!data) {
    return null;
  }

  const { secondLevelDomain } = data;
  const workspaceUrl = secondLevelDomain ? generateGpMainUrl(secondLevelDomain) : null;

  return (
    <Flex
      flexDirection="row"
      alignItems="center"
      gap="SM"
      className={styles.clientWorkspaceCell}
    >
      <Logo
        className={styles.logo}
        size="x-large"
        name={data.arenaName || "Client"}
        aria-label={`arenaImage ${data.arenaId}`}
      />
      <Flex
        flexDirection="column"
        gap={0}
        justifyContent="center"
        className={styles.clientInfo}
      >
        <Text variant="strong-md">{data.arenaName || "—"}</Text>
        {workspaceUrl && (
          <Link
            href={`${workspaceUrl}/dashboard`}
            shouldOpenInNewTab
            aria-label="Log in to client workspace"
            className={styles.workspaceUrl}
          >
            {secondLevelDomain}.junipersquare.com
            <Icon type="external-link" size="small" />
          </Link>
        )}
      </Flex>
    </Flex>
  );
};

export const ArenaIdCell: React.FC<ClientCellRendererParams> = ({ value }) => {
  return (
    <Flex alignItems="center" className={styles.arenaIdCell}>
      <Text variant="regular-md">{value || "—"}</Text>
    </Flex>
  );
};

export const ClickableCountCell: React.FC<ClickableCellParams> = ({
  value,
  data,
  column,
  dueDateRangeKey,
  isActiveWorkflows,
  useWorkflowEngine = false,
}) => {
  const numericValue = typeof value === "number" ? value : 0;

  if (value === null || value === undefined || typeof value !== "number") {
    return (
      <Flex alignItems="center" className={`${styles.cellWithPadding} ${styles.emptyValue}`}>
        —
      </Flex>
    );
  }

  if (numericValue === 0) {
    return (
      <Flex alignItems="center" className={styles.cellWithPadding}>
        <Text>{numericValue}</Text>
      </Flex>
    );
  }

  const filters = buildClientUrlFilters(
    data!,
    useWorkflowEngine,
    isActiveWorkflows ? undefined : dueDateRangeKey,
  );
  const params = new URLSearchParams();
  params.set(URL_PARAMS.FILTERS_PARAM, JSON.stringify(filters));
  const workflowsHref = `${WORKFLOWS_ROUTE}?${params.toString()}`;

  const tooltipContent = "View Tickets";
  const tooltipId = `view-workflows-tooltip-${column?.getColId()}`;

  return (
    <Flex alignItems="center" className={styles.cellWithPadding}>
      <Flex
        className={styles.clickableCellWrapper}
        justifyContent="space-between"
        alignItems="center"
      >
        <Text>{numericValue}</Text>
        <Flex alignItems="center" className={styles.hoverIconContainer}>
          <HelpIndicator id={tooltipId} content={tooltipContent}>
            <Link
              to={workflowsHref}
              className={styles.hoverIcon}
              data-testid="view-tickets-icon"
              iconVariant="outline"
              iconSize="medium"
              icon="external-link"
              color={Colors.Primary600}
            >
              {""}
            </Link>
          </HelpIndicator>
        </Flex>
      </Flex>
    </Flex>
  );
};

const getRiskClass = (value: number) => {
  if (value <= 1) return styles.risk01;
  if (value <= 3) return styles.risk23;
  if (value <= 5) return styles.risk45;
  if (value <= 7) return styles.risk67;
  if (value <= 9) return styles.risk89;
  return styles.risk10Plus;
};

export const RiskColoredCell: React.FC<ClickableCellParams> = (props) => {
  const { value } = props;
  const riskClass = getRiskClass(typeof value === "number" ? value : 0);

  return (
    <Flex alignItems="center" className={`${styles.riskHighlight} ${riskClass}`}>
      <ClickableCountCell {...props} />
    </Flex>
  );
};

export const OverdueCell = RiskColoredCell;
